// ═══════════════════════════════════════════════════════════════════════════════
// Theme + font size from user settings (light / dark / system + five-level font scale)
// ═══════════════════════════════════════════════════════════════════════════════

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { FONT_SIZE, isFontSize, THEME, type FontSize, type Theme } from '@fdh/shared';
import { syncBrowserChromeTheme, syncPlayTwaStatusBar } from '../lib/browserChromeTheme';
import { useAuth } from './AuthContext';

const THEME_STORAGE_KEY = 'fdh-ui-theme';
const FONT_STORAGE_KEY = 'fdh-ui-font-size';

const readStoredTheme = (): Theme | null => {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === THEME.LIGHT || raw === THEME.DARK || raw === THEME.SYSTEM) return raw;
  } catch {
    // ignore
  }
  return null;
};

const readStoredFontSize = (): FontSize | null => {
  try {
    const raw = localStorage.getItem(FONT_STORAGE_KEY);
    if (raw === 'xxlarge') return FONT_SIZE.XLARGE;
    if (raw && isFontSize(raw)) return raw;
  } catch {
    // ignore
  }
  return null;
};

/** Maps removed `xxlarge` to `xlarge` for stale API or localStorage values. */
const resolveFontSizePreference = (
  fromUser: string | undefined,
  stored: FontSize | null
): FontSize => {
  if (fromUser === 'xxlarge') return FONT_SIZE.XLARGE;
  if (fromUser && isFontSize(fromUser)) return fromUser;
  return stored ?? FONT_SIZE.MEDIUM;
};

type ThemeContextValue = {
  theme: Theme;
  fontSize: FontSize;
  /** Effective dark mode (includes system preference when theme is system) */
  isDark: boolean;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useThemeUi = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useThemeUi must be used within ThemeProvider');
  }
  return ctx;
};

type ThemeProviderProps = {
  children: ReactNode;
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { user, isLoading } = useAuth();
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false
  );

  const themeFromUser = user?.settings?.theme;
  const fontFromUser = user?.settings?.fontSize;

  const theme: Theme = user
    ? themeFromUser === THEME.LIGHT || themeFromUser === THEME.DARK || themeFromUser === THEME.SYSTEM
      ? themeFromUser
      : readStoredTheme() ?? THEME.SYSTEM
    : isLoading
      ? readStoredTheme() ?? THEME.SYSTEM
      : THEME.SYSTEM;

  const fontSize: FontSize = resolveFontSizePreference(fontFromUser, readStoredFontSize());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setSystemDark(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const isDark = useMemo(() => {
    if (theme === THEME.DARK) return true;
    if (theme === THEME.LIGHT) return false;
    return systemDark;
  }, [theme, systemDark]);

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDark]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
      localStorage.setItem(FONT_STORAGE_KEY, fontSize);
    } catch {
      // ignore
    }
  }, [theme, fontSize, isLoading]);

  useEffect(() => {
    syncBrowserChromeTheme(isDark, theme);
    if (isLoading) {
      return;
    }
    syncPlayTwaStatusBar(isDark, theme);
  }, [isDark, theme, isLoading]);

  useEffect(() => {
    document.documentElement.dataset.fontSize = fontSize;
  }, [fontSize]);

  const value = useMemo(
    () => ({
      theme,
      fontSize,
      isDark,
    }),
    [theme, fontSize, isDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
